import { ColumnDef } from "@tanstack/react-table";
import React, { Dispatch, SetStateAction, useMemo } from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { Comment, TABLE_IDS } from "../../../constants/common";
import { formattedDateTimeWithoutSec } from "../../../lib/functions";

interface CommentsTableProps {
  comments: Comment[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const CommentsTable = ({
  comments,
  pages,
  page,
  setPage,
  count,
  pageSize,
  setPageSize,
}: CommentsTableProps) => {
  const { t, i18n } = useTranslation();

  const columns: ColumnDef<Comment>[] = useMemo(
    () => [
      {
        accessorKey: "n_CommentType",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("commentType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_CommentType_En
                : row.original.s_CommentType_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "n_RoleID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("commentBy")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {!row.original.s_RoleName_En
                ? t("visitor")
                : i18n.language === "en"
                ? row.original.s_RoleName_En
                : row.original.s_RoleName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_Comment",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("comment")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Comment || "-"}</span>;
        },
      },
      {
        accessorKey: "s_CommentDate",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("commentDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {formattedDateTimeWithoutSec(row.original.s_CommentDate)}
            </span>
          );
        },
      },
    ],
    [i18n.language, t]
  );

  return (
    <TableWrapper
      data={comments}
      columns={columns}
      page={page}
      setPage={setPage}
      totalPages={pages}
      count={count}
      tableId={TABLE_IDS.COMMENTS}
      pageSize={pageSize}
      setPageSize={setPageSize}
    />
  );
};

export default React.memo(CommentsTable);
